import { ArrowUpRight, ArrowRight } from "lucide-react";
import WaveCanvas from "./WaveCanvas";

export default function Hero() {
  const scrollTo = (href) => (e) => {
    e.preventDefault();
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="home"
      data-testid="hero-section"
      className="relative min-h-screen flex flex-col justify-center overflow-hidden nl-grid-bg"
    >
      {/* Animated waves */}
      <div className="absolute inset-0 opacity-[0.55]">
        <WaveCanvas />
      </div>
      {/* Vignettes / overlays for legibility */}
      <div className="absolute inset-0 nl-hero-vignette" />
      <div className="absolute inset-0 bg-gradient-to-b from-[#050505]/40 via-[#050505]/30 to-[#050505]" />
      <div className="absolute inset-0 bg-[#050505]/35" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 w-full">
        {/* Top hairline + meta */}
        <div className="flex items-center gap-4 mb-16 md:mb-24 pt-28 md:pt-32">
          <span className="font-mono text-[10px] md:text-[11px] tracking-[0.28em] uppercase text-zinc-400 nl-fade-up">
            R&amp;D Incubator
          </span>
          <span className="flex-1 h-px bg-white/10 nl-line-sweep" />
          <span className="font-mono text-[10px] md:text-[11px] tracking-[0.28em] uppercase text-zinc-500 nl-fade-up">
            Canada · 2026
          </span>
        </div>

        <div className="max-w-5xl">
          <h1
            data-testid="hero-headline"
            className="font-heading text-[13vw] sm:text-6xl md:text-7xl lg:text-[88px] leading-[0.95] tracking-tighter font-medium text-white nl-fade-up"
            style={{ animationDelay: "0.1s" }}
          >
            Advanced{" "}
            <span className="text-zinc-400">Computational Methods</span>
            <br className="hidden sm:block" /> for Complex Systems.
          </h1>

          <p
            data-testid="hero-subheadline"
            className="mt-8 md:mt-10 max-w-2xl text-base md:text-lg text-zinc-300 leading-relaxed font-light nl-fade-up"
            style={{ animationDelay: "0.25s" }}
          >
            Noeton Labs develops research-driven computational approaches for
            sensing, modeling, optimization, machine learning, and intelligent
            decision support — building flexible scientific foundations for
            long-term innovation.
          </p>

          <div
            className="mt-10 md:mt-14 flex flex-wrap items-center gap-4 nl-fade-up"
            style={{ animationDelay: "0.4s" }}
          >
            <a
              href="#research"
              onClick={scrollTo("#research")}
              data-testid="hero-cta-primary"
              className="group inline-flex items-center gap-3 bg-white text-black px-7 py-4 font-mono text-[11px] tracking-[0.22em] uppercase hover:bg-zinc-200 transition-colors"
            >
              Learn More
              <ArrowRight
                size={14}
                className="transition-transform group-hover:translate-x-1"
              />
            </a>
            <a
              href="#contact"
              onClick={scrollTo("#contact")}
              data-testid="hero-cta-secondary"
              className="group inline-flex items-center gap-3 border border-white/20 text-white px-7 py-4 font-mono text-[11px] tracking-[0.22em] uppercase hover:bg-white/5 hover:border-white/40 transition-colors"
            >
              Contact Us
              <ArrowUpRight
                size={14}
                className="transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
              />
            </a>
          </div>
        </div>

      </div>

      {/* Bottom metadata strip — anchored to the section bottom */}
      <div className="hidden lg:flex absolute z-10 bottom-8 left-12 right-12 items-end justify-between text-zinc-500 pointer-events-none">
        <div className="font-mono text-[10px] tracking-[0.28em] uppercase flex items-center gap-3">
          <span className="w-6 h-px bg-zinc-600" />
          Scroll
        </div>
        <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-right leading-[1.8]">
          01 — Computational Modeling
          <br />
          02 — Sensing &amp; Inference
          <br />
          03 — Optimization &amp; ML
          <br />
          04 — Decision Support
        </div>
      </div>
    </section>
  );
}
